import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  CheckCircle2, Loader2, XCircle, FileText, Home, Gift, Copy, Check,
  Calendar, Clock, MapPin, ShoppingBag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/layout/Layout";
import { COMPANY_FISCAL_DATA, IGIC_RATE, VERIFACTU_TEXT } from "@/constants/fiscal";
import { toast } from "sonner";

interface OrderDetails {
  id: string;
  order_number: number;
  order_type: string;
  status: string;
  payment_method: string;
  payment_status: string;
  total: number;
  created_at: string;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  pickup_date?: string;
  pickup_time?: string;
  pickup_location?: string;
  observations?: string;
  loyalty_qr_token?: string;
  loyalty_short_code?: string;
  points_redeemed?: boolean;
  items?: Array<{
    product_name: string;
    quantity: number;
    unit_price: number;
    subtotal: number;
  }>;
}

const PedidoDetalle = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails(orderId);
    } else {
      setError("No se encontró el ID del pedido");
      setIsLoading(false);
    }
  }, [orderId]);

  const fetchOrderDetails = async (id: string) => {
    try {
      const { data, error: fetchError } = await supabase.functions.invoke("get-order-details", {
        body: { orderId: id }
      });

      if (fetchError || !data?.success) {
        setError(data?.error || "Pedido no encontrado");
        setIsLoading(false);
        return;
      }

      setOrder(data.order);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setError("Error al cargar el pedido");
      setIsLoading(false);
    }
  };

  const handleCopyCode = useCallback(() => {
    if (order?.loyalty_short_code) {
      navigator.clipboard.writeText(order.loyalty_short_code);
      setCodeCopied(true);
      toast.success("Código copiado");
      setTimeout(() => setCodeCopied(false), 2000);
    }
  }, [order?.loyalty_short_code]);

  const handleDownloadReceipt = useCallback(() => {
    if (!order) return;
    
    const printWindow = window.open("", "_blank", "width=302,height=800");
    if (!printWindow) return;
    
    const formatDate = (dateStr: string) => format(new Date(dateStr), "dd/MM/yyyy", { locale: es });
    const formatTime = (dateStr: string) => format(new Date(dateStr), "HH:mm:ss", { locale: es });
    const year = new Date(order.created_at).getFullYear();
    const invoiceNumber = `WEB-${year}-${order.order_number.toString().padStart(8, "0")}`;
    const totalWithTax = order.total;
    const baseImponible = totalWithTax / (1 + IGIC_RATE);
    const cuotaIGIC = totalWithTax - baseImponible;
    
    const itemsHtml = order.items?.map((item) => 
      `<tr><td class="qty">${item.quantity}</td><td class="desc">${item.product_name}</td><td class="price">${item.unit_price.toFixed(2)}€</td><td class="subtotal">${item.subtotal.toFixed(2)}€</td></tr>`
    ).join("") || '<tr><td colspan="4">Sin artículos</td></tr>';
    
    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Factura ${invoiceNumber}</title><style>@page { size: 80mm auto; margin: 0; } body { font-family: monospace; font-size: 11px; width: 80mm; padding: 3mm; color: #000; } .header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 8px; } .items-table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 10px; } .items-table th { text-align: left; border-bottom: 1px solid #000; } .items-table td.price, .items-table td.subtotal { text-align: right; } .payment-info { text-align: center; padding: 8px 0; margin-top: 6px; border: 2px solid #000; background: #e8f5e9; font-weight: bold; }</style></head><body><div class="header"><div style="font-size:18px;font-weight:bold;">PASTELERÍA REYES</div><div>${COMPANY_FISCAL_DATA.razonSocial}<br/>CIF: ${COMPANY_FISCAL_DATA.cif}</div></div><div style="text-align:center;margin:8px 0;"><strong>FACTURA SIMPLIFICADA</strong><br/>${invoiceNumber}<br/>${formatDate(order.created_at)} | ${formatTime(order.created_at)}</div><table class="items-table"><thead><tr><th>Ud.</th><th>Desc.</th><th>P.U</th><th>Total</th></tr></thead><tbody>${itemsHtml}</tbody></table><div style="border-top:1px solid #000;padding-top:8px;"><div style="display:flex;justify-content:space-between;"><span>Base:</span><span>${baseImponible.toFixed(2)}€</span></div><div style="display:flex;justify-content:space-between;"><span>IGIC (7%):</span><span>${cuotaIGIC.toFixed(2)}€</span></div><div style="display:flex;justify-content:space-between;font-weight:bold;font-size:14px;"><span>TOTAL:</span><span>${totalWithTax.toFixed(2)}€</span></div></div><div class="payment-info">✓ PAGADO ONLINE</div><div style="text-align:center;font-size:9px;margin-top:8px;">${VERIFACTU_TEXT}</div></body></html>`;
    
    printWindow.document.write(html);
    printWindow.document.close();
  }, [order]);

  const formatPickupDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "EEEE, d 'de' MMMM", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const pointsEarned = order ? Math.floor(order.total) : 0;

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2">Cargando pedido...</span>
        </div>
      </Layout>
    );
  }

  if (error || !order) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center text-center p-4">
          <XCircle className="h-16 w-16 text-destructive mb-4" />
          <p className="text-lg mb-4">{error || "Pedido no encontrado"}</p>
          <Button onClick={() => navigate("/")}>
            <Home className="mr-2 h-4 w-4" />
            Volver al inicio
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-green-50/50 to-background py-8 px-4">
        <div className="max-w-lg mx-auto space-y-6">
          
          {/* Header */}
          <div className="text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground">Pedido #{order.order_number}</h1>
            <Badge variant="outline" className="mt-2 bg-green-50 text-green-700 border-green-200">
              ✓ Pagado
            </Badge>
          </div>

          {/* Pickup Info */}
          {order.pickup_date && (
            <Card className="border-primary/20">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Calendar className="h-5 w-5 text-primary" />
                  <span className="font-semibold">{formatPickupDate(order.pickup_date)}</span>
                </div>
                {order.pickup_time && (
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <span>{order.pickup_time}</span>
                  </div>
                )}
                {order.pickup_location && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <span>{order.pickup_location}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Products */}
          {order.items && order.items.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShoppingBag className="h-4 w-4" />
                  Tu pedido
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-2">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{item.quantity}x {item.product_name}</span>
                      <span className="font-medium">{item.subtotal.toFixed(2)}€</span>
                    </div>
                  ))}
                  <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                    <span>Total</span>
                    <span className="text-primary">{order.total.toFixed(2)}€</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Loyalty Points Section */}
          {order.loyalty_qr_token && !order.points_redeemed && (
            <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50">
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-lg flex items-center justify-center gap-2 text-amber-800">
                  <Gift className="h-5 w-5" />
                  ¡Gana {pointsEarned} puntos!
                </CardTitle>
                <p className="text-sm text-amber-700">Escanea o usa el código en Club Reyes</p>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                {/* QR Code */}
                <div className="bg-white p-4 rounded-xl inline-block shadow-sm">
                  <QRCodeSVG
                    value={JSON.stringify({ type: "ticket", token: order.loyalty_qr_token })}
                    size={160}
                    level="M"
                    includeMargin={false}
                  />
                </div>

                {/* Short Code */}
                {order.loyalty_short_code && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Código manual:</p>
                    <div className="flex items-center justify-center gap-2">
                      <code className="text-2xl font-bold tracking-widest bg-white px-4 py-2 rounded-lg border-2 border-amber-200">
                        {order.loyalty_short_code}
                      </code>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleCopyCode}
                        className="h-10 w-10"
                      >
                        {codeCopied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  Abre <span className="font-medium">pasteleriareyes.com/club</span> para canjear
                </p>
              </CardContent>
            </Card>
          )}

          {/* Already Redeemed Notice */}
          {order.points_redeemed && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-green-700 font-medium">Puntos ya canjeados</p>
                <p className="text-sm text-green-600">+{pointsEarned} puntos sumados a tu cuenta</p>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="space-y-3">
            <Button 
              variant="outline" 
              onClick={handleDownloadReceipt} 
              className="w-full"
            >
              <FileText className="mr-2 h-4 w-4" />
              Descargar Factura Simplificada
            </Button>
            
            <Button onClick={() => navigate("/")} className="w-full">
              <Home className="mr-2 h-4 w-4" />
              Volver al inicio
            </Button>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default PedidoDetalle;
